// layouts/certificaciones/index.jsx
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import ListPage from "ui/ListPage";
import ConfirmDialog from "ui/ConfirmDialog";
import SnackbarAlert from "ui/SnackbarAlert";

import {
  listCertificaciones,
  deleteCertificacion,
  downloadCertPdf,
} from "./services/certificaciones";
import buildTable from "./dataCertificaciones";
import api from "services/api";

import { useAuth } from "layouts/auth/AuthContext";

import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import SoftTypography from "components/SoftTypography";

/* ================= BUSCADOR ================= */

// Campos donde aplica [$contains]
const CONTAINS_FIELDS = [
  "artist",
  "title",
  "album",
  "award",
  "catalog_identity",
  "nationality",
];

// Campos exactos/numéricos
const EXACT_FIELDS = [
  "record_label_id",
  "performer_id",
  "format_id",
  "group_id",
  "sale_units",
  "created_by",
];

// Alias usuario → API
const SEARCH_PARAM_MAP = {
  artista: "artist",
  artist: "artist",
  titulo: "title",
  title: "title",
  album: "album",
  premio: "award",
  award: "award",
  catalogo: "catalog_identity",
  catalog_identity: "catalog_identity",
  pais: "nationality",
  nacionalidad: "nationality",
  sello: "record_label_id",
  record_label_id: "record_label_id",
  interprete_id: "performer_id",
  performer_id: "performer_id",
  formato: "format_id",
  format_id: "format_id",
  grupo: "group_id",
  group_id: "group_id",
  publico: "is_public",
  estatus: "status",
  desde: "release_from",
  hasta: "release_to",
};

const normalizeBoolean = (v) =>
  /^(1|true|sí|si|yes|y|publico|public|on)$/i.test(String(v)) ? 1 : 0;

const normalizeStatus = (v) => {
  const s = String(v || "").trim().toLowerCase();

  if (/^(1|aceptado|accepted|ok|aprobado)$/.test(s)) return 1;
  if (/^(2|rechazado|rejected)$/.test(s)) return 2;
  if (/^(3|pendiente|pending)$/.test(s)) return 3;

  return null;
};

const isNumeric = (v) => /^-?\d+(\.\d+)?$/.test(String(v).trim());

const toArray = (v) => (Array.isArray(v) ? v : []);

// ¿La búsqueda es sólo texto libre (sin clave:valor)?
function extractFreeText(input = "") {
  const s = String(input || "").trim();
  if (!s) return "";
  // si contiene al menos un "clave:valor", no es texto libre
  const hasPairs = /\b[^:\s]+:/.test(s);
  if (hasPairs) return "";
  return s.replace(/^"|"$/g, "");
}

// dedupe por id preservando orden
function dedupeById(arr = []) {
  const seen = new Set();
  const out = [];
  for (const it of arr) {
    const id = it?.id ?? it?._id;
    if (id == null) continue;
    if (!seen.has(id)) {
      seen.add(id);
      out.push(it);
    }
  }
  return out;
}

function buildSearchParams(input = "") {
  const s = String(input || "").trim();
  const params = {};
  if (!s) return params;

  const tokens = s.match(/\S+:"[^"]+"|\S+:\S+|".+?"|\S+/g) || [];
  const pairs = [];
  const free = [];

  for (const tok of tokens) {
    const m = tok.match(/^([^:]+):(.+)$/);
    if (m) {
      const rawKey = m[1].toLowerCase().trim();
      let rawVal = m[2].trim();
      if (rawVal.startsWith('"') && rawVal.endsWith('"')) {
        rawVal = rawVal.slice(1, -1);
      }
      const apiKey = SEARCH_PARAM_MAP[rawKey] || rawKey;
      pairs.push([apiKey, rawVal]);
    } else {
      free.push(tok.replace(/^"|"$/g, ""));
    }
  }

  // pares clave:valor
  for (const [key, value] of pairs) {
    if (key === "release_from") {
      params["release_date[$gte]"] = value;
      continue;
    }
    if (key === "release_to") {
      params["release_date[$lte]"] = value;
      continue;
    }

    if (key === "is_public") {
      params[key] = normalizeBoolean(value);
      continue;
    }

    if (key === "status") {
      const normalized = normalizeStatus(value);
      if (normalized !== null) params[key] = String(normalized);
      continue;
    }

    if (EXACT_FIELDS.includes(key) || isNumeric(value)) {
      params[key] = value;
      continue;
    }

    if (CONTAINS_FIELDS.includes(key)) params[`${key}[$contains]`] = value;
    else params[key] = value;
  }

  // texto libre → OR en title / artist / album
  if (free.length) {
    const q = free.join(" ").replace(/\s+/g, " ").trim();
    const OR_FIELDS = ["title", "artist", "album"];
    OR_FIELDS.forEach((field, i) => {
      params[`$or[${i}][${field}][$contains]`] = q;
    });
  }

  return params;
}

/* ============ SORT MAP ============ */
/**
 * Mapa entre nombre de columna (UI) y campo real en la BD
 */
const SORT_FIELD_MAP = {
  titulo: "title",
  artista: "artist",
  sello: "record_label_id",
  award: "award",
  creado: "created_at",
};

const statusChip = (s) => {
  const v = Number(s);

  if (v === 1) {
    return <Chip size="small" color="success" label="Aceptado" variant="outlined" />;
  }

  if (v === 2) {
    return <Chip size="small" color="error" label="Rechazado" variant="outlined" />;
  }

  if (v === 3) {
    return <Chip size="small" color="warning" label="Pendiente" variant="outlined" />;
  }

  return <Chip size="small" color="default" label="Sin estatus" variant="outlined" />;
};

const safeName = (s = "") => String(s).trim().replace(/[^\w\-]+/g, "_");

// Comparador para ordenar el merge en búsquedas de texto libre
function compareByField(a, b, field, direction = "asc") {
  let res = 0;
  const dir = direction === "asc" ? 1 : -1;

  switch (field) {
    case "created_at": {
      const da = new Date(a.created_at ?? a.createdAt ?? 0);
      const db = new Date(b.created_at ?? b.createdAt ?? 0);
      const ta = da.getTime();
      const tb = db.getTime();
      res = ta - tb;
      break;
    }
    case "title": {
      res = String(a.title ?? "").localeCompare(String(b.title ?? ""), "es", {
        sensitivity: "base",
      });
      break;
    }
    case "artist": {
      res = String(a.artist ?? "").localeCompare(
        String(b.artist ?? ""),
        "es",
        { sensitivity: "base" }
      );
      break;
    }
    case "record_label_id": {
      const va = Number(a.record_label_id ?? 0);
      const vb = Number(b.record_label_id ?? 0);
      res = va - vb;
      break;
    }
    case "award": {
      res = String(a.award ?? "").localeCompare(String(b.award ?? ""), "es", {
        sensitivity: "base",
      });
      break;
    }
    default:
      res = 0;
  }

  return dir * res;
}

async function fetchAllRecordLabels() {
  const pageSize = 1000;
  let all = [];
  let offset = 0;

  for (;;) {
    const { data } = await api.get("/recordLabel", {
      params: {
        __limit: pageSize,
        __offset: offset,
      },
    });

    const page = Array.isArray(data)
      ? data
      : Array.isArray(data?.data)
      ? data.data
      : Array.isArray(data?.page)
      ? data.page
      : Array.isArray(data?.results)
      ? data.results
      : [];

    if (!page.length) break;

    all = all.concat(page);

    if (page.length < pageSize) break;

    offset += page.length;
  }

  return all;
}

function getRecordLabelGroupIds(label) {
  if (Array.isArray(label?.groups) && label.groups.length) {
    return label.groups
      .map((g) =>
        typeof g === "object" && g !== null
          ? g.id ?? g._id
          : g
      )
      .filter(
        (id) =>
          id !== null &&
          id !== undefined &&
          id !== ""
      )
      .map(String);
  }

  // compatibilidad con sellos antiguos
  if (
    label?.group_id !== null &&
    label?.group_id !== undefined &&
    label?.group_id !== ""
  ) {
    return [String(label.group_id)];
  }

  return [];
}

/* ================= COMPONENTE ================= */

export default function Certificaciones() {
  const navigate = useNavigate();
  const location = useLocation();
  const initialSearch = location.state?.initialSearch ?? "";

  const [search, setSearch] = useState(() => {
    return location.state?.initialSearch || "";
  });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // paginación server-side
  const [page, setPage] = useState(0); // 0-based
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);

  // sort (por defecto: creado DESC)
  const [sort, setSort] = useState({
    uiField: "creado",
    apiField: "created_at",
    direction: "desc",
  });

  // filtro de estatus: "all" | "pending" | "accepted" | "rejected"
  const [statusFilter, setStatusFilter] = useState("all");

  // catálogos lazy: id → nombre
  const [formatMap, setFormatMap] = useState({});
  const [labelMap, setLabelMap] = useState({});

  const [pending, setPending] = useState(null);
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const openSnack = (m, s = "success") =>
    setSnack({ open: true, message: m, severity: s });

  const closeSnack = () => setSnack((s) => ({ ...s, open: false }));

  const [downloadingId, setDownloadingId] = useState(null);

  const { user, baseUser, isImpersonating } = useAuth();

  const perms = user?.permissions?.certificaciones || {};

  const isAdmin =
    baseUser?.is_admin === true ||
    baseUser?.is_admin === 1 ||
    String(baseUser?.is_admin).toLowerCase() === "1" ||
    String(baseUser?.is_admin).toLowerCase() === "true";

  const actingAsAdmin = isAdmin && !isImpersonating;

  const canRead =
    actingAsAdmin ||
    !!perms.read ||
    !!perms.create ||
    !!perms.update ||
    !!perms.delete;

  const canCreate =
    actingAsAdmin || !!perms.create;

  const canUpdate =
    actingAsAdmin || !!perms.update;

  const canDelete =
    actingAsAdmin || !!perms.delete;

  // El grupo pertenece al usuario efectivo.
  // Solo se utilizará para restringir cuando NO sea admin
  // o cuando el admin esté impersonando.
  const assignedGroupIds = useMemo(() => {
    if (Array.isArray(user?.groups) && user.groups.length) {
      return [
        ...new Set(
          user.groups
            .map((g) =>
              typeof g === "object" && g !== null
                ? g.id ?? g._id
                : g
            )
            .filter(
              (id) =>
                id !== null &&
                id !== undefined &&
                id !== ""
            )
            .map(String)
        ),
      ];
    }

    // Compatibilidad temporal con usuarios antiguos
    if (
      user?.group_id !== null &&
      user?.group_id !== undefined &&
      user?.group_id !== ""
    ) {
      return [String(user.group_id)];
    }

    return [];
  }, [user?.groups, user?.group_id]);

  const [allRecordLabels, setAllRecordLabels] = useState([]);
  const [recordLabelsReady, setRecordLabelsReady] = useState(false);

  useEffect(() => {
    let cancel = false;

    (async () => {
      try {
        const labels = await fetchAllRecordLabels();

        if (!cancel) {
          setAllRecordLabels(labels || []);
          setRecordLabelsReady(true);
        }
      } catch (e) {
        if (!cancel) {
          setErr(
            e?.response?.data?.message ||
            e?.message ||
            "No se pudieron cargar los sellos."
          );
          setRecordLabelsReady(true);
        }
      }
    })();

    return () => {
      cancel = true;
    };
  }, []);

  const allowedRecordLabelIds = useMemo(() => {
    // Admin normal puede ver todos
    if (actingAsAdmin) {
      return [];
    }

    if (!assignedGroupIds.length) {
      return [];
    }

    return allRecordLabels
      .filter((label) => {
        const labelGroupIds = getRecordLabelGroupIds(label);

        return labelGroupIds.some((groupId) =>
          assignedGroupIds.includes(String(groupId))
        );
      })
      .map((label) => String(label.id ?? label._id))
      .filter(Boolean);
  }, [
    actingAsAdmin,
    assignedGroupIds,
    allRecordLabels,
  ]);

  /* ============ FETCH PRINCIPAL (lista) ============ */

  const fetchList = async (opts = {}) => {
    const mustRestrictByLabel = !actingAsAdmin;

      if (
        mustRestrictByLabel &&
        !recordLabelsReady
      ) {
        return;
      }

      if (
        mustRestrictByLabel &&
        allowedRecordLabelIds.length === 0
      ) {
        setItems([]);
        setTotal(0);
        setLoading(false);
        return;
      }

      const enforcedLabels = {};

      if (mustRestrictByLabel) {
        allowedRecordLabelIds.forEach((recordLabelId, index) => {
          enforcedLabels[
            `record_label_id[$in][${index}]`
          ] = recordLabelId;
        });
      }

    const {
      pageIndex = page,
      pageSize = rowsPerPage,
      currentSearch = search,
      sortField = sort.apiField,
      sortDirection = sort.direction,
    } = opts;

    setLoading(true);
    setErr("");

    try {
      const limit = pageSize;
      const offset = pageIndex * pageSize;

      // sort flatten: "__sort[created_at]" → -1/1
      const sortKey = sortField ? `__sort[${sortField}]` : null;
      const sortParams =
        sortKey != null ? { [sortKey]: sortDirection === "asc" ? 1 : -1 } : {};

      // statusFilter → status para la API
      // como string para evitar que "0" se pierda en serializers que eliminan falsy
      const statusParam = {};
      if (statusFilter === "accepted") statusParam.status = "1";
      else if (statusFilter === "rejected") statusParam.status = "2";
      else if (statusFilter === "pending") statusParam.status = "3";

      const freeQ = extractFreeText(currentSearch);

      // 1) Texto libre → OR (title, artist) con merge en front
      if (freeQ) {
        const need = (pageIndex + 1) * pageSize;

        const baseParams = {
          __limit: need,
          __offset: 0,
          ...sortParams,
          ...statusParam,
          ...enforcedLabels,
        };

        const titleParams = {
          ...baseParams,
          "title[$contains]": freeQ,
        };

        const artistParams = {
          ...baseParams,
          "artist[$contains]": freeQ,
        };

        const [byTitle, byArtist] = await Promise.all([
          listCertificaciones(titleParams),
          listCertificaciones(artistParams),
        ]);

        const rowsTitle = Array.isArray(byTitle?.rows)
          ? byTitle.rows
          : Array.isArray(byTitle?.page)
          ? byTitle.page
          : [];

        const rowsArtist = Array.isArray(byArtist?.rows)
          ? byArtist.rows
          : Array.isArray(byArtist?.page)
          ? byArtist.page
          : [];

        let merged = dedupeById([...rowsTitle, ...rowsArtist]);

        if (sortField) {
          merged = merged.sort((a, b) =>
            compareByField(a, b, sortField, sortDirection)
          );
        }

        const start = offset;
        const end = start + limit;
        const pageSlice = merged.slice(start, end);

        setItems(pageSlice);

        const totalTitle = Number(byTitle?.total || 0);
        const totalArtist = Number(byArtist?.total || 0);
        setTotal(totalTitle + totalArtist);
        return;
      }

      // 2) Búsqueda con clave:valor
      const searchParams = buildSearchParams(currentSearch);

      // Evita que un NO-admin filtre otros grupos manualmente
      if (!actingAsAdmin) {
        delete searchParams.group_id;
        delete searchParams["group_id[$eq]"];
      }

      if (statusFilter === "all" && "status" in searchParams) {
        delete searchParams.status;
      }

      const listRes = await listCertificaciones({
        __limit: limit,
        __offset: offset,
        ...sortParams,
        ...searchParams,
        ...statusParam,
        ...enforcedLabels,
      });

      const rowsData =
        listRes?.rows ??
        listRes?.page ??
        (Array.isArray(listRes) ? listRes : []);

      setItems(rowsData);
      setTotal(Number(listRes?.total ?? rowsData.length));
    } catch (e) {
      setErr(e?.response?.data?.message || e.message || String(e));
    } finally {
      setLoading(false);
    }
  };

  // Aplica filtro inicial cuando vengas desde el dashboard
  useEffect(() => {
    const fromState = location.state?.initialSearch;

    if (
      typeof fromState === "string" &&
      fromState !== "" &&
      fromState !== search
    ) {
      setSearch(fromState);
      setPage(0);

      fetchList({
        pageIndex: 0,
        pageSize: rowsPerPage,
        currentSearch: fromState,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.initialSearch]);

  useEffect(() => {
    const rawSearch = location.state?.initialSearch;
    if (typeof rawSearch !== "string" || !rawSearch) return;

    const m = rawSearch.match(/estatus:(".*?"|\S+)/i);
    if (!m) return;

    const rawVal = m[1].replace(/^"|"$/g, "");
    const n = normalizeStatus(rawVal);

    if (n === 1) setStatusFilter("accepted");
    else if (n === 2) setStatusFilter("rejected");
    else if (n === 3) setStatusFilter("pending");
  }, [location.state?.initialSearch]);

  /* ============ LAZY LOAD de formatos / sellos ============ */
  useEffect(() => {
    if (!items.length) return;

    const missingFormatIds = new Set();
    const missingLabelIds = new Set();

    items.forEach((it) => {
      const fid = it.format_id ?? it.formatId;
      const lid = it.record_label_id ?? it.recordLabelId ?? it.label_id;

      if (fid != null && formatMap[String(fid)] == null) {
        missingFormatIds.add(String(fid));
      }
      if (lid != null && labelMap[String(lid)] == null) {
        missingLabelIds.add(String(lid));
      }
    });

    if (!missingFormatIds.size && !missingLabelIds.size) return;

    let cancel = false;

    (async () => {
      try {
        const [formats, labels] = await Promise.all([
          missingFormatIds.size
            ? Promise.all(
                Array.from(missingFormatIds).map((id) =>
                  api
                    .get(`/format/${encodeURIComponent(id)}`)
                    .then((r) => r.data?.data ?? r.data)
                    .catch(() => null)
                )
              )
            : [],
          missingLabelIds.size
            ? Promise.all(
                Array.from(missingLabelIds).map((id) =>
                  api
                    .get(`/recordLabel/${encodeURIComponent(id)}`)
                    .then((r) => r.data?.data ?? r.data)
                    .catch(() => null)
                )
              )
            : [],
        ]);

        if (cancel) return;

        if (formats.length) {
          setFormatMap((prev) => {
            const next = { ...prev };
            formats.forEach((f) => {
              if (!f) return;
              const id = String(f.id ?? f._id ?? "").trim();
              if (!id || next[id] != null) return;
              next[id] = f.name ?? f.nombre ?? `Formato ${id}`;
            });
            return next;
          });
        }

        if (labels.length) {
          setLabelMap((prev) => {
            const next = { ...prev };
            labels.forEach((l) => {
              if (!l) return;
              const id = String(l.id ?? l._id ?? "").trim();
              if (!id || next[id] != null) return;
              next[id] = l.name ?? l.nombre ?? `Sello ${id}`;
            });
            return next;
          });
        }
      } catch (e) {
        console.warn("[CERT/INDEX] error resolviendo catálogos:", e);
      }
    })();

    return () => {
      cancel = true;
    };
  }, [items, formatMap, labelMap]);

  /* ============ EFECTOS DE CARGA INICIAL / REFRESH ============ */

  useEffect(() => {
    if (!actingAsAdmin && !recordLabelsReady) {
      return;
    }

    fetchList({
      pageIndex: 0,
      pageSize: rowsPerPage,
      sortField: sort.apiField,
      sortDirection: sort.direction,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    actingAsAdmin,
    recordLabelsReady,
    allowedRecordLabelIds,
  ]);

  useEffect(() => {
    if (location.state?.refresh) {
      fetchList({
        pageIndex: page,
        pageSize: rowsPerPage,
        currentSearch: search,
        sortField: sort.apiField,
        sortDirection: sort.direction,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.refresh]);

  useEffect(() => {
    const onFocus = () =>
      fetchList({
        pageIndex: page,
        pageSize: rowsPerPage,
        currentSearch: search,
        sortField: sort.apiField,
        sortDirection: sort.direction,
      });

    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [page, rowsPerPage, search, sort.apiField, sort.direction, statusFilter]);

  /* ============ MAPEO A FILAS (tabla) ============ */

  const { columns: baseCols, rows } = useMemo(
    () =>
      buildTable(toArray(items), (it) => {
        const id = it.id ?? it._id;
        const statusNum = Number(it.status);
        

        const selloName =
          labelMap[String(it.record_label_id)] ??
          it.record_label_name ??
          (it.record_label && typeof it.record_label === "object"
            ? it.record_label.name ?? it.record_label.nombre
            : null) ??
          (typeof it.record_label === "string" &&
          !it.record_label.toLowerCase().endsWith(".pdf")
            ? it.record_label
            : null) ??
          "—";

        const accepted = statusNum === 1;

        return {
          id,
          titulo: it.title ?? "-",
          artista: it.artist ?? "-",
          sello: selloName,
          award: it.award ?? "—",
          creado: it.created_at ? String(it.created_at) : "-",
          estatus: statusChip(statusNum),

          pdf: accepted ? (
            <Tooltip title="Descargar certificado PDF">
              <span>
                <IconButton
                  size="small"
                  aria-label="Descargar certificado PDF"
                  onClick={(e) => {
                    e.stopPropagation();
                    const fname = `certificado_${safeName(
                      it.artist || "cert"
                    )}_${safeName(it.title || "")}.pdf`;

                    setDownloadingId(id);
                    downloadCertPdf(id, fname)
                      .then(() => openSnack("Descarga iniciada."))
                      .catch((err) =>
                        openSnack(err?.message || String(err), "error")
                      )
                      .finally(() => setDownloadingId(null));
                  }}
                  disabled={downloadingId === id}
                >
                  {downloadingId === id ? (
                    <CircularProgress size={18} />
                  ) : (
                    <PictureAsPdfIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          ) : null,

          onEdit: canUpdate
            ? () => navigate(`/certificaciones/${id}/editar`)
            : null,
          onDelete: canDelete
            ? () => setPending({ id, label: it.title ?? `#${id}` })
            : null,
        };
      }),
    [items, labelMap, navigate, downloadingId, canUpdate, canDelete]
  );

  /* ============ BÚSQUEDA (debounce) ============ */
  useEffect(() => {
    const id = setTimeout(() => {
      setPage(0);
      fetchList({
        pageIndex: 0,
        pageSize: rowsPerPage,
        currentSearch: search,
        sortField: sort.apiField,
        sortDirection: sort.direction,
      });
    }, 250);

    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, rowsPerPage, sort.apiField, sort.direction, statusFilter]);

  /* ============ PAGINACIÓN ============ */

  const handleChangePage = (_e, newPage) => {
    setPage(newPage);
    fetchList({
      pageIndex: newPage,
      pageSize: rowsPerPage,
      currentSearch: search,
      sortField: sort.apiField,
      sortDirection: sort.direction,
    });
  };

  const handleChangeRowsPerPage = (e) => {
    const newSize = parseInt(e.target.value, 10);
    setRowsPerPage(newSize);
    setPage(0);
    fetchList({
      pageIndex: 0,
      pageSize: newSize,
      currentSearch: search,
      sortField: sort.apiField,
      sortDirection: sort.direction,
    });
  };

  /* ============ ORDENAMIENTO POR COLUMNA ============ */

  const handleSortChange = (columnName) => {
    const apiField = SORT_FIELD_MAP[columnName] || null;

    setPage(0);

    setSort((prev) => {
      let direction = "asc";

      if (prev.uiField === columnName && prev.direction === "asc") {
        direction = "desc";
      }

      const next = {
        uiField: columnName,
        apiField,
        direction,
      };

      fetchList({
        pageIndex: 0,
        pageSize: rowsPerPage,
        currentSearch: search,
        sortField: next.apiField,
        sortDirection: next.direction,
      });

      return next;
    });
  };

  /* ============ ELIMINAR ============ */

  const confirmDelete = async () => {
    if (!pending) return;

    try {
      await deleteCertificacion(pending.id);
      setItems((prev) => prev.filter((x) => (x.id ?? x._id) !== pending.id));
      openSnack(`Eliminado: ${pending.label}`, "success");

      fetchList({
        pageIndex: page,
        pageSize: rowsPerPage,
        currentSearch: search,
        sortField: sort.apiField,
        sortDirection: sort.direction,
      });
    } catch (e) {
      const msg = e?.response?.data?.message || e.message || String(e);
      setErr(msg);
      openSnack(`No se pudo eliminar: ${msg}`, "error");
    } finally {
      setPending(null);
    }
  };

  /* ============ RENDER ============ */

  if (!canRead) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <Box p={3}>
          <SoftTypography variant="h6">
            No tienes permisos para ver certificaciones.
          </SoftTypography>
        </Box>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <ListPage
        title="Certificaciones"
        search={search}
        onSearchChange={setSearch}
        addText={canCreate ? "Agregar" : undefined}
        onAdd={canCreate ? () => navigate("/certificaciones/nuevo") : undefined}
        columns={baseCols}
        rows={rows}
        loading={loading}
        error={err}
        cardSx={{ maxWidth: "100%", mx: 0 }}
        sortField={sort.uiField}
        sortDirection={sort.direction}
        onSortChange={handleSortChange}
        paginationProps={
          !loading && {
            count: total,
            page,
            rowsPerPage,
            onPageChange: handleChangePage,
            onRowsPerPageChange: handleChangeRowsPerPage,
            rowsPerPageOptions: [5, 10, 25, 50, 100],
            showFirstButton: true,
            showLastButton: true,
          }
        }
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
          }}
        >
          <SoftTypography
            variant="caption"
            color="text"
            sx={{ fontWeight: 500 }}
          >
            Estatus:
          </SoftTypography>

          <ToggleButtonGroup
            exclusive
            size="small"
            value={statusFilter}
            onChange={(_e, val) => {
              if (!val) return;
              setStatusFilter(val);
            }}
          >
            <ToggleButton value="all">Todos</ToggleButton>
            <ToggleButton value="pending">Pendiente</ToggleButton>
            <ToggleButton value="accepted">Aceptado</ToggleButton>
            <ToggleButton value="rejected">Rechazado</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </ListPage>

      <Footer />

      <ConfirmDialog
        open={!!pending}
        title="Eliminar certificación"
        description={`¿Seguro que quieres eliminar "${pending?.label}"?`}
        confirmText="Eliminar"
        onClose={() => setPending(null)}
        onConfirm={confirmDelete}
      />

      <SnackbarAlert
        open={snack.open}
        onClose={closeSnack}
        message={snack.message}
        severity={snack.severity}
      />
    </DashboardLayout>
  );
}